import type {
  Attachment,
  Bucket,
  Comment,
  Id,
  Label,
  Paginated,
  Project,
  ProjectView,
  Task,
  TaskQuery,
  User,
} from '@shared/types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuthError() {
    return this.status === 401;
  }
}

/**
 * All mutating requests carry this header. The server rejects unsafe methods
 * without it, which is what makes the httpOnly session cookie CSRF-safe — a
 * cross-site form can't set custom headers. See server/auth.ts.
 */
const CSRF_HEADER = { 'X-Requested-With': 'ecotone-tasks' } as const;

async function request<T>(
  path: string,
  { method = 'GET', body }: { method?: string; body?: unknown } = {},
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    // Session lives in an httpOnly cookie, so it must be sent explicitly.
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(method === 'GET' ? {} : CSRF_HEADER),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    let message = res.statusText;
    try {
      const err = (await res.json()) as { error?: string };
      if (err.error) message = err.error;
    } catch {
      /* non-JSON body */
    }
    throw new ApiError(res.status, message);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/** Serialises a TaskQuery into the query string the server expects. */
function taskQueryString(q: TaskQuery): string {
  const p = new URLSearchParams();
  if (q.projectId !== undefined) p.set('projectId', String(q.projectId));
  if (q.includeSubprojects) p.set('includeSubprojects', 'true');
  if (q.done !== undefined) p.set('done', String(q.done));
  if (q.undated) p.set('undated', 'true');
  if (q.assigneeIds?.length) p.set('assigneeIds', q.assigneeIds.join(','));
  if (q.labelIds?.length) p.set('labelIds', q.labelIds.join(','));
  if (q.search) p.set('search', q.search);
  if (q.dueBefore) p.set('dueBefore', q.dueBefore);
  if (q.dueAfter) p.set('dueAfter', q.dueAfter);
  if (q.sort) p.set('sort', q.sort);
  if (q.order) p.set('order', q.order);
  if (q.page) p.set('page', String(q.page));
  if (q.perPage) p.set('perPage', String(q.perPage));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export type BucketWithTasks = Bucket & { tasks: Task[] };

export const api = {
  login: (username: string, password: string, totp?: string) =>
    request<{ user: User }>('/auth/login', {
      method: 'POST',
      body: { username, password, ...(totp ? { totp } : {}) },
    }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  requestReset: (email: string) =>
    request<{ ok: true }>('/auth/request-reset', { method: 'POST', body: { email } }),

  resetPassword: (token: string, password: string) =>
    request<{ user: User }>('/auth/reset', { method: 'POST', body: { token, password } }),

  me: () => request<{ user: User }>('/me'),

  notificationPrefs: () => request<{ prefs: NotificationPrefs }>('/me/notifications'),
  setNotificationPrefs: (prefs: NotificationPrefs) =>
    request<{ prefs: NotificationPrefs }>('/me/notifications', { method: 'PUT', body: prefs }),
  sendTestEmail: () => request<{ ok: true; to: string }>('/me/notifications/test', { method: 'POST' }),

  projects: (archived = false) =>
    request<{ items: Project[]; tree: Project[] }>(`/projects?archived=${archived}`),

  project: (id: Id) =>
    request<{ project: Project; views: ProjectView[] }>(`/projects/${id}`),

  buckets: (projectId: Id, viewId: Id) =>
    request<{ buckets: BucketWithTasks[]; unassigned: Task[] }>(
      `/projects/${projectId}/views/${viewId}/buckets`,
    ),

  tasks: (q: TaskQuery) => request<Paginated<Task>>(`/tasks${taskQueryString(q)}`),

  moveTask: (viewId: Id, taskId: Id, bucketId: Id | null, beforeTaskId?: Id | null) =>
    request<{ ok: true }>(`/views/${viewId}/move`, {
      method: 'POST',
      body: { taskId, bucketId, beforeTaskId },
    }),

  task: (id: Id) => request<{ task: Task; comments: Comment[] }>(`/tasks/${id}`),

  updateTask: (id: Id, patch: TaskPatch) =>
    request<{ task: Task }>(`/tasks/${id}`, { method: 'PATCH', body: patch }),

  createTask: (projectId: Id, title: string, description?: string) =>
    request<{ task: Task }>('/tasks', {
      method: 'POST',
      body: { projectId, title, description },
    }),

  addComment: (taskId: Id, comment: string) =>
    request<{ comment: Comment }>(`/tasks/${taskId}/comments`, {
      method: 'POST',
      body: { comment },
    }),

  uploadAttachment: async (taskId: Id, file: File) => {
    // multipart, so this bypasses the JSON `request` helper but keeps the CSRF
    // header and same-origin cookie.
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`/api/tasks/${taskId}/attachments`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { ...CSRF_HEADER },
      body: form,
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, err.error ?? res.statusText);
    }
    return (await res.json()) as { attachment: Attachment };
  },

  labels: () => request<{ items: Label[] }>('/labels'),

  users: () => request<{ items: User[] }>('/users'),
};

export interface NotificationPrefs {
  onAssigned: boolean;
  onComment: boolean;
  dueDigest: boolean;
  dueSoon: boolean;
}

/** The write-side shape: relations go by id, not whole objects. */
export interface TaskPatch {
  title?: string;
  description?: string | null;
  done?: boolean;
  dueDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  priority?: number;
  percentDone?: number;
  bucketId?: Id | null;
  position?: number;
  hexColor?: string | null;
  isFavorite?: boolean;
  assigneeIds?: Id[];
  labelIds?: Id[];
}
